import { z } from 'zod';

// =============================================================================
//  SaasProduct schemas — Story 2.3 (CRUD)
//  DR-013 Catálogo central, DR-015 Multi-conta Asaas, DR-016 Níveis de integração
// =============================================================================

export const saasProductTypeSchema = z.enum([
  'saas_mensal',
  'servico_unico',
  'consultoria_recorrente',
  'implantacao',
  'produto_digital',
  'desenvolvimento_personalizado',
]);
export type SaasProductTypeSchema = z.infer<typeof saasProductTypeSchema>;

export const saasProductStatusSchema = z.enum([
  'ativo',
  'em_desenvolvimento',
  'pausado',
  'descontinuado',
]);
export type SaasProductStatusSchema = z.infer<typeof saasProductStatusSchema>;

export const saasEnvironmentSchema = z.enum([
  'producao',
  'homologacao',
  'interno',
  'cliente_dedicado',
]);
export type SaasEnvironmentSchema = z.infer<typeof saasEnvironmentSchema>;

export const integrationLevelSchema = z.enum(['manual', 'read_only', 'full']);
export type IntegrationLevelSchema = z.infer<typeof integrationLevelSchema>;

/**
 * Como o cliente entra na compra (peça 3 do funil único). `subject` = só com o
 * passe assinado pelo SaaS e o acesso vai pro usuário + empresa dele; `email` =
 * compra anônima por e-mail.
 */
export const saasIdentityModeSchema = z.enum(['email', 'subject']);
export type SaasIdentityModeSchema = z.infer<typeof saasIdentityModeSchema>;

const SLUG_REGEX = /^[a-z0-9_-]+$/;
const slugField = z
  .string()
  .min(1, 'Slug obrigatório.')
  .max(64, 'Slug deve ter até 64 caracteres.')
  .regex(SLUG_REGEX, 'Slug aceita só minúsculas, números, hífen e underscore.');

const urlField = z
  .union([z.string().url('URL inválida.'), z.literal('')])
  .optional()
  .nullable();

// api_url é alvo de requests server-side (provisioning/import/health) → exige
// https no write-time (E14.2). O bloqueio de IP interno (SSRF) é em runtime via
// assertSafeOutboundUrl; aqui pegamos cedo o erro óbvio de http://.
const apiUrlField = z
  .union([
    z
      .string()
      .url('URL inválida.')
      .refine((u) => u.toLowerCase().startsWith('https://'), 'api_url deve usar https.'),
    z.literal(''),
  ])
  .optional()
  .nullable();

const uuidField = z.string().uuid('UUID inválido.').optional().nullable();

/**
 * Base shape — campos editáveis. NÃO inclui `slug` (que é imutável em update).
 */
export const saasProductMutableBaseSchema = z.object({
  name: z.string().min(1, 'Nome obrigatório.').max(200, 'Nome deve ter até 200 caracteres.'),
  description_short: z.string().max(280).optional().nullable(),
  description_long: z.string().max(5000).optional().nullable(),
  type: saasProductTypeSchema.default('saas_mensal'),
  status: saasProductStatusSchema.default('ativo'),
  url: urlField,
  admin_url: urlField,
  // Logo do cabecalho dos e-mails deste produto. O nome do remetente sai do
  // `name` — texto livre, nao exige dominio verificado no Resend.
  email_logo_url: urlField,
  // Pagina de assinatura DENTRO do SaaS — destino do botao "continuar
  // assinando" nos e-mails. Preferida ao checkout do CC porque o cliente chega
  // no dominio que ele conhece e ja autenticado. Vazio = usa o checkout do CC.
  subscription_url: urlField,
  environment: saasEnvironmentSchema.default('producao'),
  technical_user_id: uuidField,
  commercial_user_id: uuidField,
  observations: z.string().max(5000).optional().nullable(),
  integration_level: integrationLevelSchema.default('manual'),
  // Ausente no update = não mexe (o .partial() tira o default): tela velha que
  // não manda o campo nunca tira o Reservei do modo "login do sistema".
  identity_mode: saasIdentityModeSchema.default('email'),
  asaas_account_id: uuidField,
  api_url: apiUrlField,
  bootstrap_api_key: z.string().max(500).optional().nullable(),
  webhook_secret: z.string().max(500).optional().nullable(),
  rate_limit_per_min: z.coerce.number().int().min(1).max(10_000).default(100),
});

/**
 * Create schema — adiciona slug obrigatório.
 */
export const createSaasProductSchema = saasProductMutableBaseSchema.extend({
  slug: slugField,
});
export type CreateSaasProductDto = z.output<typeof createSaasProductSchema>;
export type CreateSaasProductInput = z.input<typeof createSaasProductSchema>;

/**
 * Update schema — partial sobre o base + version obrigatória.
 * IMPORTANTE: `slug` NÃO está aqui — slug é imutável após criação (D1).
 * Secrets `bootstrap_api_key` e `webhook_secret`: se enviados em branco/null,
 * mantêm valor existente (não sobrescrevem); se com valor, substituem.
 */
export const updateSaasProductSchema = saasProductMutableBaseSchema
  .partial()
  .extend({
    version: z.number().int().positive('version obrigatório (optimistic locking).'),
  })
  .strict()
  // Strict pega slug e qualquer campo desconhecido — exceto o que está no base.
  .refine((data) => !('slug' in data), {
    message: 'Slug é imutável após criação.',
    path: ['slug'],
  });
export type UpdateSaasProductDto = z.output<typeof updateSaasProductSchema>;
export type UpdateSaasProductInput = z.input<typeof updateSaasProductSchema>;

/**
 * Query schema — GET /api/saas-products
 */
export const querySaasProductsSchema = z.object({
  cursor: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: saasProductStatusSchema.optional(),
  type: saasProductTypeSchema.optional(),
  integration_level: integrationLevelSchema.optional(),
  q: z.string().min(1).max(200).optional(),
});
export type QuerySaasProductsDto = z.infer<typeof querySaasProductsSchema>;

/**
 * Delete schema — usado tanto pelo body do DELETE quanto pelo header If-Match merge.
 */
export const deleteSaasProductSchema = z.object({
  version: z.number().int().positive('version obrigatório (optimistic locking).'),
});
export type DeleteSaasProductDto = z.infer<typeof deleteSaasProductSchema>;
